/**
 * File management API helpers — attachments + share bundles.
 *
 * Shape mirrors the worker mappers in worker/src/routes/attachments.ts
 * and worker/src/routes/share-bundles.ts.
 */
import { api, apiRequest } from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export const FILE_TYPES = [
  'dieline', 'spec_sheet', 'mockup', 'print_proof', 'photo', 'artwork', 'other',
] as const;
export type FileType = (typeof FILE_TYPES)[number];

export const FILE_STATES = ['working', 'master', 'superseded'] as const;
export type FileState = (typeof FILE_STATES)[number];

export const FILE_STATUSES = ['approved', 'pending', 'rejected'] as const;
export type FileStatus = (typeof FILE_STATUSES)[number];

export const FILE_SOURCES = ['internal', 'vendor', 'customer', 'inbound_email'] as const;
export type FileSource = (typeof FILE_SOURCES)[number];

export const FILE_TYPE_LABELS: Record<FileType, string> = {
  dieline: 'Dielines',
  spec_sheet: 'Spec Sheets',
  mockup: 'Mockups',
  print_proof: 'Print Proofs',
  photo: 'Photos',
  artwork: 'Artwork',
  other: 'Other',
};

export const FILE_TYPE_OPTIONS: Array<{ value: FileType; label: string }> =
  FILE_TYPES.map((t) => ({ value: t, label: FILE_TYPE_LABELS[t] }));

export interface Attachment {
  id: string;
  orgId: string;
  entityType: string;
  entityId: string;
  filename: string;
  contentType: string | null;
  size: number | null;
  storageKey: string;
  uploadedBy: string | null;
  fileType: FileType | null;
  fileState: FileState;
  status: FileStatus;
  version: number;
  supersedesAttachmentId: string | null;
  sourceType: FileSource | null;
  sourceVendorId: string | null;
  sourceCustomerId: string | null;
  sourceNotes: string | null;
  description: string | null;
  sortOrder: number | null;
  isDeleted: boolean;
  deletedAt: string | null;
  deletedBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  createdAt: string;
}

export interface UploadSlot {
  uploadUrl: string;
  storageKey: string;
  attachmentId: string;
  maxBytes: number;
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------
export function listAttachments(entityType: string, entityId: string, sessionId?: string | null) {
  return api.get<Attachment[]>(`/attachments/${entityType}/${encodeURIComponent(entityId)}`, sessionId);
}

export function requestUploadUrl(input: {
  fileName: string;
  fileSize?: number;
  mimeType: string;
  entityType: string;
  entityId: string;
}, sessionId?: string | null) {
  return api.post<UploadSlot>('/attachments/request-upload-url', input, sessionId);
}

/**
 * Streams the file bytes to the worker. Returns the {storageKey, size} response.
 */
export async function uploadFileBytes(
  uploadUrl: string,
  file: File | Blob,
  sessionId?: string | null,
): Promise<{ storageKey: string; size: number; contentType: string }> {
  const headers: Record<string, string> = {
    'Content-Type': (file as File).type || 'application/octet-stream',
  };
  if (sessionId) headers['Authorization'] = `Bearer ${sessionId}`;
  const res = await fetch(uploadUrl, { method: 'POST', headers, body: file });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || 'Upload failed');
  return json.data;
}

export function createAttachment(input: {
  entityType: string;
  entityId: string;
  filename: string;
  storageKey: string;
  contentType?: string;
  size?: number;
  fileType?: FileType | null;
  fileState?: FileState;
  status?: FileStatus;
  description?: string | null;
  sourceType?: FileSource | null;
  sourceVendorId?: string | null;
  sourceCustomerId?: string | null;
  sourceNotes?: string | null;
  supersedesAttachmentId?: string | null;
}, sessionId?: string | null) {
  return api.post<Attachment>('/attachments', input, sessionId);
}

export function updateAttachment(
  id: string,
  patch: Partial<Pick<Attachment,
    'filename' | 'fileType' | 'fileState' | 'status' | 'description' |
    'sourceType' | 'sourceVendorId' | 'sourceCustomerId' | 'sourceNotes' | 'sortOrder'
  >>,
  sessionId?: string | null,
) {
  return api.patch<Attachment>(`/attachments/${id}`, patch, sessionId);
}

export function deleteAttachment(id: string, sessionId?: string | null) {
  return api.delete<{ id: string; deleted: boolean }>(`/attachments/${id}`, sessionId);
}

export function listAttachmentVersions(id: string, sessionId?: string | null) {
  return api.get<Attachment[]>(`/attachments/${id}/versions`, sessionId);
}

export function downloadAttachmentUrl(id: string, sessionId?: string | null): string {
  // The worker download endpoint reads the bearer token from the Authorization
  // header — links can't carry headers, so this returns a path the caller can
  // either fetch + blob-download or pop open as a hidden iframe with the
  // session header. For the FilesTab preview pane we'll use fetch+blob.
  void sessionId;
  return `/api/attachments/${id}/download`;
}

export async function fetchAttachmentBlob(id: string, sessionId?: string | null): Promise<Blob> {
  const res = await fetch(`/api/attachments/${id}/download`, {
    headers: sessionId ? { Authorization: `Bearer ${sessionId}` } : undefined,
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  return res.blob();
}

// ---------------------------------------------------------------------------
// Share bundles
// ---------------------------------------------------------------------------
export type GateMode = 'email_required' | 'link_only';
export type BundleStatus = 'draft' | 'active' | 'revoked';

export interface ShareBundle {
  id: string;
  orgId: string;
  name: string;
  message: string | null;
  token: string;
  gateMode: GateMode;
  status: BundleStatus;
  expiresAt: string | null;
  downloadCap: number;
  downloadCount: number;
  lastAccessedAt: string | null;
  sentAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShareBundleSummary extends ShareBundle {
  itemCount: number;
  recipientCount: number;
}

export interface ShareBundleItem {
  id: string;
  bundleId: string;
  attachmentId: string;
  sortOrder: number;
  addedBy: string | null;
  addedAt: string;
  attachment: {
    id: string;
    filename: string;
    contentType: string | null;
    size: number | null;
    fileType: FileType | null;
    fileState: FileState;
    version: number;
  } | null;
}

export interface ShareBundleRecipient {
  id: string;
  bundleId: string;
  email: string;
  name: string | null;
  recipientToken: string;
  emailedAt: string | null;
  firstAccessedAt: string | null;
  lastAccessedAt: string | null;
  accessCount: number;
  createdAt: string;
}

export interface ShareBundleAccessLog {
  id: string;
  bundleId: string;
  recipientId: string | null;
  recipientEmail: string | null;
  attachmentId: string | null;
  action: string;
  bytesServed: number | null;
  ipAddress: string | null;
  userAgent: string | null;
  notes: string | null;
  createdAt: string;
}

export interface ShareBundleDetail {
  bundle: ShareBundle;
  items: ShareBundleItem[];
  recipients: ShareBundleRecipient[];
  log: ShareBundleAccessLog[];
}

export interface ActiveDraftPointer {
  bundleId: string;
  name: string;
  itemCount: number;
}

export function listBundles(opts: { status?: BundleStatus } = {}, sessionId?: string | null) {
  const qs = opts.status ? `?status=${opts.status}` : '';
  return api.get<ShareBundleSummary[]>(`/share-bundles${qs}`, sessionId);
}

export function getBundle(id: string, sessionId?: string | null) {
  return api.get<ShareBundleDetail>(`/share-bundles/${id}`, sessionId);
}

export function createBundle(input: {
  name: string;
  message?: string | null;
  expiresAt?: string | null;
  downloadCap?: number;
  gateMode?: GateMode;
  attachmentIds?: string[];
  recipients?: Array<{ email: string; name?: string | null }>;
}, sessionId?: string | null) {
  return api.post<ShareBundle>('/share-bundles', input, sessionId);
}

export function updateBundle(id: string, patch: Partial<{
  name: string;
  message: string | null;
  expiresAt: string | null;
  downloadCap: number;
  gateMode: GateMode;
}>, sessionId?: string | null) {
  return api.patch<ShareBundle>(`/share-bundles/${id}`, patch, sessionId);
}

export function deleteBundle(id: string, sessionId?: string | null) {
  return api.delete<{ ok: boolean }>(`/share-bundles/${id}`, sessionId);
}

export function revokeBundle(id: string, sessionId?: string | null) {
  return api.post<ShareBundle>(`/share-bundles/${id}/revoke`, {}, sessionId);
}

export function addBundleItems(id: string, attachmentIds: string[], sessionId?: string | null) {
  return api.post<{ added: number; skippedOrgMismatch: number }>(
    `/share-bundles/${id}/items`, { attachmentIds }, sessionId,
  );
}

export function reorderBundleItems(id: string, itemIds: string[], sessionId?: string | null) {
  return api.post<{ ok: true }>(`/share-bundles/${id}/items/reorder`, { itemIds }, sessionId);
}

export function removeBundleItem(id: string, itemId: string, sessionId?: string | null) {
  return api.delete<{ ok: true }>(`/share-bundles/${id}/items/${itemId}`, sessionId);
}

export function addBundleRecipients(id: string, recipients: Array<{ email: string; name?: string | null }>, sessionId?: string | null) {
  return api.post<ShareBundleRecipient[]>(`/share-bundles/${id}/recipients`, { recipients }, sessionId);
}

export function removeBundleRecipient(id: string, recipientId: string, sessionId?: string | null) {
  return api.delete<{ ok: true }>(`/share-bundles/${id}/recipients/${recipientId}`, sessionId);
}

export function sendBundle(id: string, recipientIds?: string[], sessionId?: string | null) {
  return api.post<{ sent: number; totalTargeted: number }>(
    `/share-bundles/${id}/send`, recipientIds ? { recipientIds } : {}, sessionId,
  );
}

export function getActiveDraft(sessionId?: string | null) {
  return api.get<ActiveDraftPointer | null>('/share-bundles/active-draft', sessionId);
}

export function setActiveDraft(bundleId: string, sessionId?: string | null) {
  return api.post<{ bundleId: string; name: string }>('/share-bundles/active-draft', { bundleId }, sessionId);
}

export function clearActiveDraft(sessionId?: string | null) {
  return api.delete<{ ok: true }>('/share-bundles/active-draft', sessionId);
}

// ---------------------------------------------------------------------------
// Public (no auth) — used by /share/:token page
// ---------------------------------------------------------------------------
export async function publicGetBundle(token: string) {
  const res = await fetch(`/api/public/share-bundles/${token}`, { credentials: 'include' });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || 'Failed to load share');
  return json.data as {
    bundleName: string;
    orgName: string;
    expired: boolean;
    revoked: boolean;
    requiresEmail: boolean;
    verifiedEmail: string | null;
    message?: string | null;
    items?: Array<{
      id: string; attachmentId: string; fileName: string;
      mimeType: string | null; sizeBytes: number | null;
    }>;
    downloadsRemaining?: number;
  };
}

export async function publicVerifyEmail(token: string, email: string) {
  const res = await fetch(`/api/public/share-bundles/${token}/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || 'Verification failed');
  return json.data as { ok: true };
}

export async function publicView(token: string) {
  void apiRequest; // re-use already-typed import keep
  await fetch(`/api/public/share-bundles/${token}/view`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function publicDownloadUrl(token: string, itemId: string): string {
  return `/api/public/share-bundles/${token}/items/${itemId}/download`;
}

// ---------------------------------------------------------------------------
// React Query keys (canonical)
// ---------------------------------------------------------------------------
export const fileKeys = {
  list: (entityType: string, entityId: string) => ['attachments', entityType, entityId] as const,
  versions: (entityType: string, entityId: string, id: string) =>
    ['attachments', entityType, entityId, 'versions', id] as const,
  bundlesList: (status?: BundleStatus) => ['share-bundles', { status }] as const,
  bundle: (id: string) => ['share-bundles', id] as const,
  activeDraft: () => ['share-bundles', 'active-draft'] as const,
};
